/**
 * Why the user is looking at prices.
 *
 * When a feature gate fires it knows two things the pricing page otherwise
 * never learns: which plan actually unlocks the feature, and which feature it
 * was. Losing that between the two screens is expensive. Measured 2026-08-09
 * over 90 days, in sessions that hit a Pro-only gate (3D view, 3D PDF export,
 * AI planner) the completed checkouts were:
 *
 *     standard 23  ·  premium 13  ·  pro 6
 *
 * So 86% paid for a plan that does NOT unlock what they came for. They do not
 * find out until after the card is charged, which is the worst possible moment.
 *
 * The intent travels two ways because users take two routes:
 *   - in the URL (`?required=pro&feature=3d_view`) for the explicit link, and
 *   - in sessionStorage for everything else. That second path carries most of
 *     the traffic: of 298 Pro-gate sessions, 287 dismissed the modal and only
 *     26 clicked it, yet 35 still made their own way to /pricing.
 *
 * sessionStorage, not localStorage: this is about the current visit. Someone
 * returning next week should see the normal pricing page, not one still framed
 * by a gate they hit days ago.
 */

export type UpgradePlanId = 'lite' | 'standard' | 'premium' | 'pro' | 'enterprise_duo' | 'enterprise';

/** Plan ordering used by every "is this plan enough" comparison. Keep this the
 *  single copy: a second, drifting ladder is how a paywall silently turns into
 *  a failed purchase. `free`/`trial` sit at 0 so any paid plan outranks them. */
export const PLAN_RANK: Record<string, number> = {
  trial: 0,
  free: 0,
  lite: 1,
  standard: 2,
  premium: 3,
  pro: 4,
  // Duo unlocks exactly what Enterprise unlocks (it differs in seats, and
  // seats are not a gate), so for coverage the two share a rank. The buy
  // direction between them lives in PricingPage.planHierarchy, not here.
  enterprise_duo: 6,
  enterprise: 6,
};

const VALID_PLANS: readonly UpgradePlanId[] = ['lite', 'standard', 'premium', 'pro', 'enterprise_duo', 'enterprise'];

export interface UpgradeIntent {
  /** Lowest plan that unlocks the feature the user was blocked on. */
  required: UpgradePlanId;
  /** Gate identifier, e.g. '3d_view'. Used for the on-page explanation. */
  feature?: string;
}

const STORAGE_KEY = 'cctv_upgrade_intent_v1';

function isPlanId(v: string | null | undefined): v is UpgradePlanId {
  return !!v && (VALID_PLANS as readonly string[]).includes(v);
}

/** Record why the paywall opened, so /pricing can explain itself even when the
 *  user gets there by their own route rather than a link we control. */
export function saveUpgradeIntent(intent: UpgradeIntent): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      required: intent.required,
      ...(intent.feature ? { feature: intent.feature.slice(0, 64) } : {}),
    }));
  } catch {
    // Private mode or storage disabled. The URL path still works; degrade quietly.
  }
}

/**
 * Read the intent, URL first so an explicit link always beats a stale session
 * value. `search` is injectable for tests; defaults to the live query string.
 * Returns null when there is no trustworthy intent, which means "render the
 * ordinary pricing page".
 */
export function readUpgradeIntent(search?: string): UpgradeIntent | null {
  if (typeof window === 'undefined') return null;

  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const required = params.get('required');
    if (isPlanId(required)) {
      const feature = params.get('feature');
      return { required, ...(feature ? { feature: feature.slice(0, 64) } : {}) };
    }
  } catch {
    // Malformed query string — fall through to the stored value.
  }

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { required?: string; feature?: string };
    if (!isPlanId(parsed.required)) return null;
    return {
      required: parsed.required,
      ...(parsed.feature ? { feature: String(parsed.feature).slice(0, 64) } : {}),
    };
  } catch {
    return null;
  }
}

export function clearUpgradeIntent(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the value simply outlives the tab.
  }
}

/** Query string for a pricing link that keeps the context, e.g.
 *  `?required=pro&feature=3d_view`. Empty string when there is no intent. */
export function upgradeIntentSearch(intent: UpgradeIntent | null): string {
  if (!intent) return '';
  const params = new URLSearchParams({ required: intent.required });
  if (intent.feature) params.set('feature', intent.feature);
  return `?${params.toString()}`;
}

/** Does `plan` unlock something that needs at least `required`? */
export function planCovers(plan: string, required: UpgradePlanId): boolean {
  return (PLAN_RANK[plan] ?? 0) >= (PLAN_RANK[required] ?? 0);
}

/**
 * Gate id → the i18n key naming that feature to a buyer.
 *
 * Mostly points at the feature strings the pricing cards already use, so the
 * wording matches what the card lists and no new translation is needed. Only
 * gates with no matching card line get their own key.
 *
 * A gate that is missing here is not a bug: the caller falls back to the raw
 * id with underscores stripped, which is ugly but still true.
 */
export const GATE_FEATURE_NAME_KEY: Record<string, string> = {
  // Every plan opens 3D a few times and the 3D dialog sells Studio, so the
  // card line is the part only Studio and up have.
  '3d_view': 'gate_feature_name_3d_unlimited',
  '3d_pdf_export': 'gate_feature_name_3d_pdf_export',
  ai_planner: 'gate_feature_name_ai_planner',
  client_portal: 'feature_client_portal',
  dxf_export: 'feature_dxf_export',
  survey_mode: 'feature_survey_mode',
  system_validator: 'feature_system_validator',
  smart_quote: 'feature_smart_quote',
};
